/** Longest edge for hero / gallery images — enough for retina full-width. */
export const WEBSITE_IMAGE_MAX_EDGE_PX = 1920;

/** Skip re-encoding when the source is already small enough for the web. */
export const WEBSITE_IMAGE_SKIP_BELOW_BYTES = 120 * 1024;

const JPEG_QUALITY = 0.82;
const WEBP_QUALITY = 0.8;

const SKIP_MIME_TYPES = new Set([
  "image/gif",
  "image/svg+xml",
  "image/x-icon",
  "image/vnd.microsoft.icon",
]);

let webpEncodeSupported: boolean | null = null;

function extensionForMime(mime: string): string {
  switch (mime) {
    case "image/webp":
      return "webp";
    case "image/png":
      return "png";
    case "image/jpeg":
      return "jpg";
    default:
      return "jpg";
  }
}

/** Swap a storage path extension to match the uploaded file type. */
export function replaceStoragePathExtension(storagePath: string, file: File): string {
  const ext = extensionForMime(file.type);
  const slash = storagePath.lastIndexOf("/");
  const dot = storagePath.lastIndexOf(".");
  if (dot <= slash) return `${storagePath}.${ext}`;
  return `${storagePath.slice(0, dot)}.${ext}`;
}

/** Scale dimensions down so the longest edge fits within `maxEdge`. */
export function scaleImageDimensions(
  width: number,
  height: number,
  maxEdge: number = WEBSITE_IMAGE_MAX_EDGE_PX,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export function shouldSkipWebsiteImageCompression(file: File): boolean {
  if (!file.type.startsWith("image/")) return true;
  if (SKIP_MIME_TYPES.has(file.type)) return true;
  if (file.size <= WEBSITE_IMAGE_SKIP_BELOW_BYTES) return true;
  return false;
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("image_decode_failed"));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("image_encode_failed"));
      },
      type,
      quality,
    );
  });
}

async function detectWebpEncodeSupport(): Promise<boolean> {
  if (webpEncodeSupported != null) return webpEncodeSupported;
  if (typeof document === "undefined") {
    webpEncodeSupported = false;
    return false;
  }
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  try {
    const blob = await canvasToBlob(canvas, "image/webp", WEBP_QUALITY);
    webpEncodeSupported = blob.type === "image/webp";
  } catch {
    webpEncodeSupported = false;
  }
  return webpEncodeSupported;
}

function buildCompressedFileName(originalName: string, mime: string): string {
  const ext = extensionForMime(mime);
  const base = originalName.replace(/\.[^.]+$/, "") || "image";
  return `${base}.${ext}`;
}

/**
 * Resize and re-encode wedding-site photos for faster public pages.
 * Returns the original file when compression is unnecessary or fails.
 */
export async function compressImageForWeb(file: File): Promise<File> {
  if (shouldSkipWebsiteImageCompression(file)) return file;

  try {
    const img = await loadImageFromFile(file);
    const { width, height } = scaleImageDimensions(img.naturalWidth, img.naturalHeight);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;

    ctx.drawImage(img, 0, 0, width, height);

    const useWebp = await detectWebpEncodeSupport();
    const mime = useWebp ? "image/webp" : "image/jpeg";
    const quality = useWebp ? WEBP_QUALITY : JPEG_QUALITY;
    const blob = await canvasToBlob(canvas, mime, quality);

    if (blob.size >= file.size) return file;

    return new File([blob], buildCompressedFileName(file.name, mime), {
      type: mime,
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
}
